import { useEffect, useRef, useState, ReactNode } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import {
  PIShell,
  Drill,
  PIInsight,
  PINoteImage,
  PracticeLadder,
} from "@/components/pi/PIShell";
import { PianoDiagram, KeyHighlight } from "@/components/PianoDiagram";
import { LessonMidi } from "@/components/MidiPlayer";
import { KeyPicker } from "@/components/KeyPicker";
import { KeyProvider, useKeyOrDefault } from "@/theory/key";
import { midiToPitchName, tonicMidiForPc } from "@/theory/melody";
import { kickAudio, playSequence } from "@/audio/piano";

/**
 * Piano Intermediate · Lesson 21 — Major Chord Shapes on the Circle of Fifths.
 *
 * 12 major chords on the piano collapse into FOUR shape categories: WWW
 * (all-white), WBW, BWB, and three "unique" outliers (BBB / WBB / BWW).
 * The Circle of Fifths organises them into clusters of three neighbours
 * that share a shape — C/F/G are all WWW; A/D/E are all WBW; A♭/D♭/E♭ are
 * all BWB; F♯ is BBB; B is WBB; B♭ is BWW.
 *
 * Bespoke visual: a Shape Wheel — the Circle of Fifths laid out as 12
 * positions on a circle, with each chord coloured by its shape category.
 * Click any position → master keyboard re-lights to show that chord's
 * shape, and a tiny glyph reveals the WWW / WBW / BWB / unique pattern.
 */

interface ChordShape {
  letter: string;
  pc: number;
  pattern: "WWW" | "WBW" | "BWB" | "BBB" | "WBB" | "BWW";
  family: 1 | 2 | 3 | 4; // 1=WWW family, 2=WBW family, 3=BWB family, 4=unique
  /** Position on circle of fifths in clockwise order from C (0..11). */
  cofPos: number;
}

const CHORDS: ChordShape[] = [
  { letter: "C",  pc: 0,  pattern: "WWW", family: 1, cofPos: 0 },
  { letter: "G",  pc: 7,  pattern: "WWW", family: 1, cofPos: 1 },
  { letter: "D",  pc: 2,  pattern: "WBW", family: 2, cofPos: 2 },
  { letter: "A",  pc: 9,  pattern: "WBW", family: 2, cofPos: 3 },
  { letter: "E",  pc: 4,  pattern: "WBW", family: 2, cofPos: 4 },
  { letter: "B",  pc: 11, pattern: "WBB", family: 4, cofPos: 5 },
  { letter: "F#", pc: 6,  pattern: "BBB", family: 4, cofPos: 6 },
  { letter: "Db", pc: 1,  pattern: "BWB", family: 3, cofPos: 7 },
  { letter: "Ab", pc: 8,  pattern: "BWB", family: 3, cofPos: 8 },
  { letter: "Eb", pc: 3,  pattern: "BWB", family: 3, cofPos: 9 },
  { letter: "Bb", pc: 10, pattern: "BWW", family: 4, cofPos: 10 },
  { letter: "F",  pc: 5,  pattern: "WWW", family: 1, cofPos: 11 },
];

const FAMILY_INFO: Record<1 | 2 | 3 | 4, { label: string; description: string; chordList: string }> = {
  1: { label: "WWW (all-white)",   description: "Every key is white — the I, IV and V of C major.",                 chordList: "C · F · G" },
  2: { label: "WBW",               description: "White – Black – White. Root and fifth are white; the third is black.", chordList: "D · A · E" },
  3: { label: "BWB",               description: "Black – White – Black. Root and fifth are black; the third is white.", chordList: "D♭ · A♭ · E♭" },
  4: { label: "Unique outliers",   description: "The remaining three chords each have their own shape — they break the family pattern.", chordList: "F♯ (all black) · B (W-B-B) · B♭ (B-W-W)" },
};

export default function PianoIntermediate21() {
  const { lesson, course, accent } = findLesson("piano-intermediate", "21");
  return (
    <LessonShell
      course={course}
      courseSlug="piano-intermediate"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <KeyProvider defaultTonic="C" availableQualities={["major"]}>
        <PIShell
          title="Major Chord Shapes — the Circle of Fifths Way"
          exerciseGoal="Twelve major chords. Three families of three. Three outliers. Master four shapes — you'll never blank on a major chord again."
          bpm={92}
          difficulty="Intermediate"
        >
          <Body />
        </PIShell>
      </KeyProvider>
    </LessonShell>
  );
}

function Body() {
  const k = useKeyOrDefault();
  const tonicMidi = tonicMidiForPc(k.tonicPc);
  const [chordIdx, setChordIdx] = useState(0);
  const chord = CHORDS[chordIdx];

  // The current chord's three voices on the master keyboard
  const masterHighlights: KeyHighlight[] = [
    { pc: chord.pc,                tone: "root",  label: "1" },
    { pc: (chord.pc + 4) % 12,     tone: "voice", label: "3" },
    { pc: (chord.pc + 7) % 12,     tone: "voice", label: "5" },
  ];

  const playChord = async () => {
    kickAudio();
    const root = tonicMidi + ((chord.pc - k.tonicPc) % 12 + 12) % 12;
    await playSequence(
      [
        {
          time: 0,
          notes: [
            midiToPitchName(root, k.useFlats),
            midiToPitchName(root + 4, k.useFlats),
            midiToPitchName(root + 7, k.useFlats),
          ],
          duration: "2n",
        },
      ],
      92
    );
  };

  // Family info derived from the active chord
  const family = FAMILY_INFO[chord.family];

  return (
    <>
      <StudyDeck>
        <KeyPicker />
        <PianoDiagram
          highlights={masterHighlights}
          octaves={2}
          startOctave={3}
          playable
          ariaLabel={`The ${chord.letter} major chord on the keyboard`}
        />
        <p className="mt-2 text-[12.5px] text-cyan-300/70">
          Master keyboard reflects the chord you click on the wheel below.
          The chord shape pattern shows beneath the wheel.
        </p>
      </StudyDeck>

      <FadeIn>
        <Drill number="1" label="The big idea" title="Don't memorise twelve chords. Memorise four shapes.">
          <p>
            There are 12 major chords. Most students try to memorise each
            one by name — and forget half of them. The trick: arrange the
            12 around the <strong>Circle of Fifths</strong>, and you'll see
            they cluster into <strong>four shape families</strong>:
          </p>
          <ul className="my-3 space-y-1 text-[14px] text-cyan-50/95">
            <li>· <strong>WWW</strong> (all white): C, F, G — the I, IV and V of C major.</li>
            <li>· <strong>WBW</strong> (white-black-white): D, A, E.</li>
            <li>· <strong>BWB</strong> (black-white-black): D♭, A♭, E♭.</li>
            <li>· <strong>Outliers</strong>: F♯ (all black), B (W-B-B), B♭ (B-W-W).</li>
          </ul>
          <PIQuote attribution="Jason · lesson video">
            "You have 12 but three of them can be grouped together and there
            are three groups of three."
          </PIQuote>
        </Drill>
      </FadeIn>

      <FadeIn>
        <Drill number="2" label="The wheel" title="Click any chord, or let it walk for you">
          <WheelControls
            chords={CHORDS}
            chordIdx={chordIdx}
            setChordIdx={setChordIdx}
          />
          <ShapeWheel
            chords={CHORDS}
            activeIdx={chordIdx}
            onPick={setChordIdx}
          />
          <div className="mt-3 grid gap-2 md:grid-cols-2">
            <div className="rounded-xl border border-amber-400/30 bg-amber-500/10 p-3">
              <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-amber-300">
                Active chord
              </div>
              <div className="mt-1 text-2xl font-bold text-amber-100">
                {chord.letter} major
              </div>
              <div className="mt-1 font-mono text-[12px] text-amber-200/90">
                Shape pattern: <strong>{chord.pattern}</strong>
              </div>
              <button
                onClick={playChord}
                className="mt-2 rounded-lg bg-amber-400 px-3 py-1.5 text-[12px] font-bold text-zinc-900 hover:bg-amber-300"
              >
                ▶ Hear it
              </button>
            </div>
            <div className="rounded-xl border border-cyan-400/30 bg-cyan-950/40 p-3">
              <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-cyan-300">
                Shape family
              </div>
              <div className="mt-1 text-[15px] font-bold text-cyan-100">
                {family.label}
              </div>
              <div className="mt-1 text-[12px] leading-snug text-cyan-200/85">
                {family.description}
              </div>
              <div className="mt-1 font-mono text-[12px] text-cyan-200/90">
                Members: {family.chordList}
              </div>
            </div>
          </div>
        </Drill>
      </FadeIn>

      <FadeIn>
        <Drill number="3" label="The Circle of Fifths trick" title="Why the families cluster">
          <p>
            Walk clockwise around the Circle of Fifths and notice: every
            chord and its two immediate neighbours form a{" "}
            <strong>I-IV-V trio</strong> in their parent key. C-F-G are
            the I-IV-V of C major. A-D-E are the I-IV-V of A major.
            A♭-D♭-E♭ are the I-IV-V of A♭ major. <strong>That's why the
            shapes cluster</strong> — neighbouring keys share their primary
            chords, and primary chords in a key tend to share keyboard
            anatomy.
          </p>
          <PIInsight>
            <p>
              This pattern is the most useful single fact you'll learn
              this lesson. Once you see it, you stop "memorising" major
              chords and start <em>recognising</em> them by neighbourhood.
            </p>
          </PIInsight>
        </Drill>
      </FadeIn>

      <FadeIn>
        <Drill number="4" label="Writing it down" title="Your hand has to write before your fingers can play">
          <p>
            Jason's repeated instruction in this lesson:{" "}
            <strong>write the Circle of Fifths down by hand</strong>. Not
            once — many times. Both directions:
          </p>
          <ul className="my-3 space-y-1 font-mono text-[13px] text-cyan-100/95">
            <li>· Clockwise: C → G → D → A → E → B → F♯ → D♭ → A♭ → E♭ → B♭ → F → C</li>
            <li>· Counter-clockwise: C → F → B♭ → E♭ → A♭ → D♭ → F♯ → B → E → A → D → G → C</li>
          </ul>
          <PIQuote attribution="Jason · lesson video">
            "Writing it yourself is very important, as they do when we are in
            school. The teacher kind of forces you to write a thing like a
            million times."
          </PIQuote>
        </Drill>
      </FadeIn>

      <FadeIn>
        <Drill number="5" label="Three outliers" title="The chords that don't fit a family">
          <p>
            <strong>F♯ major</strong> is the only all-black chord — F♯,
            A♯, C♯. <strong>B major</strong> is white-black-black — B, D♯,
            F♯. <strong>B♭ major</strong> is black-white-white — B♭, D, F.
            They don't share a family. Memorise them by their oddness.
          </p>
        </Drill>
      </FadeIn>

      <FadeIn>
        <Drill number="6" label="Play along" title="Drill all 12 in their CoF order">
          <LessonMidi
            courseSlug="piano-intermediate"
            order="21"
            label="All 12 major chord shapes"
            originalBpm={92}
          />
        </Drill>
      </FadeIn>

      <FadeIn>
        <Drill number="7" label="Practice ladder" title="Twelve minutes, four families">
          <PracticeLadder
            intro={
              <>
                One family per rung. Don't shuffle them; learn the shape
                category cleanly first, then move on. By the fourth rung
                the outliers should feel like funny relatives, not
                strangers.
              </>
            }
            rungs={[
              { title: "WWW family · C, F, G",       minutes: 3, accent: "amber",   detail: "All-white triads. Walk between them clockwise. Feel the pinky stay close to the wrist on every chord." },
              { title: "WBW family · D, A, E",       minutes: 3, accent: "cyan",    detail: "Same shape, three positions. The black third sits between two white keys. Practice the move D → A → E without looking down." },
              { title: "BWB family · D♭, A♭, E♭",   minutes: 3, accent: "rose",    detail: "Black on the outside, white in the middle. The hand actually feels MORE comfortable here — the black keys hold your fingers in shape." },
              { title: "Outliers · F♯, B, B♭",       minutes: 3, accent: "violet",  detail: "Memorise each by its oddness. F♯ is all black; B has a long stretch to F♯; B♭ wants to be a barre chord." },
            ]}
          />
        </Drill>
      </FadeIn>
    </>
  );
}

/* ── Walk-clockwise / counter-clockwise / family-filter controls ── */
function WheelControls({
  chords,
  chordIdx,
  setChordIdx,
}: {
  chords: ChordShape[];
  chordIdx: number;
  setChordIdx: (i: number) => void;
}) {
  const [walking, setWalking] = useState<"cw" | "ccw" | null>(null);
  const [familyFilter, setFamilyFilter] = useState<1 | 2 | 3 | 4 | null>(null);
  const intervalRef = useRef<number | null>(null);

  // Walk timer — moves to the next CoF position every 1.2s
  useEffect(() => {
    if (!walking) return;
    intervalRef.current = window.setInterval(() => {
      const cur = chords[chordIdx];
      const targetCofPos =
        walking === "cw"
          ? (cur.cofPos + 1) % 12
          : (cur.cofPos + 11) % 12;
      const next = chords.findIndex((c) => c.cofPos === targetCofPos);
      if (next >= 0) setChordIdx(next);
    }, 1200);
    return () => {
      if (intervalRef.current != null) window.clearInterval(intervalRef.current);
    };
  }, [walking, chordIdx, chords, setChordIdx]);

  // Family-filter walk — jump to the next chord in that family
  const jumpToFamily = (fam: 1 | 2 | 3 | 4) => {
    const cur = chords[chordIdx];
    if (cur.family === fam) {
      // already in this family — go to next member
      const family = chords.filter((c) => c.family === fam);
      const i = family.findIndex((c) => c.letter === cur.letter);
      const next = family[(i + 1) % family.length];
      setChordIdx(chords.findIndex((c) => c.letter === next.letter));
    } else {
      const idx = chords.findIndex((c) => c.family === fam);
      if (idx >= 0) setChordIdx(idx);
    }
    setFamilyFilter(fam);
    setWalking(null);
  };

  return (
    <div className="my-3 flex flex-wrap items-center gap-2">
      <button
        onClick={() => setWalking((w) => (w === "ccw" ? null : "ccw"))}
        className={`rounded-md border px-3 py-1.5 text-[12px] font-bold ${
          walking === "ccw"
            ? "border-amber-400 bg-amber-500/15 text-amber-200"
            : "border-cyan-400/40 bg-cyan-500/5 text-cyan-200 hover:bg-cyan-500/15"
        }`}
      >
        ⟲ Walk CCW
      </button>
      <button
        onClick={() => setWalking((w) => (w === "cw" ? null : "cw"))}
        className={`rounded-md border px-3 py-1.5 text-[12px] font-bold ${
          walking === "cw"
            ? "border-amber-400 bg-amber-500/15 text-amber-200"
            : "border-cyan-400/40 bg-cyan-500/5 text-cyan-200 hover:bg-cyan-500/15"
        }`}
      >
        Walk CW ⟳
      </button>
      <span className="ml-2 text-[11px] uppercase tracking-wider text-cyan-300/70">Or filter to a family:</span>
      {([1, 2, 3, 4] as const).map((fam) => {
        const active = familyFilter === fam;
        const colors: Record<1 | 2 | 3 | 4, string> = {
          1: "bg-amber-500/30 text-amber-200 border-amber-400/60",
          2: "bg-cyan-500/30 text-cyan-200 border-cyan-400/60",
          3: "bg-rose-500/30 text-rose-200 border-rose-400/60",
          4: "bg-violet-500/30 text-violet-200 border-violet-400/60",
        };
        const labels: Record<1 | 2 | 3 | 4, string> = { 1: "WWW", 2: "WBW", 3: "BWB", 4: "Outliers" };
        return (
          <button
            key={fam}
            onClick={() => jumpToFamily(fam)}
            className={`rounded-md border px-2 py-1 font-mono text-[11px] font-bold ${
              active ? colors[fam] : "border-cyan-400/30 bg-zinc-900/40 text-cyan-300/80 hover:bg-cyan-500/10"
            }`}
          >
            {labels[fam]}
          </button>
        );
      })}
    </div>
  );
}

/* ── Bespoke visual: ShapeWheel — Circle of Fifths with chord-shape colouring ── */
function ShapeWheel({
  chords,
  activeIdx,
  onPick,
}: {
  chords: ChordShape[];
  activeIdx: number;
  onPick: (i: number) => void;
}) {
  const cx = 200;
  const cy = 200;
  const r = 140;
  const familyColors: Record<1 | 2 | 3 | 4, string> = {
    1: "#F59E0B", // amber for WWW
    2: "#22D3EE", // cyan for WBW
    3: "#FB7185", // rose for BWB
    4: "#A78BFA", // violet for outliers
  };

  return (
    <div className="my-4 rounded-2xl border border-cyan-400/30 bg-zinc-900/40 p-4">
      <svg viewBox="0 0 400 400" className="w-full">
        {/* Outer ring guide */}
        <circle cx={cx} cy={cy} r={r + 18} fill="none" stroke="#475569" strokeDasharray="2 4" strokeWidth="1" />
        {/* 12 chord positions */}
        {chords.map((c, i) => {
          // 12 o'clock = top (cofPos 0); each step = -30° from there clockwise
          const angle = (c.cofPos * 30 - 90) * (Math.PI / 180);
          const x = cx + r * Math.cos(angle);
          const y = cy + r * Math.sin(angle);
          const isActive = i === activeIdx;
          return (
            <g key={c.letter} onClick={() => onPick(i)} style={{ cursor: "pointer" }}>
              <motion.circle
                cx={x}
                cy={y}
                animate={{ r: isActive ? 30 : 24 }}
                transition={{ duration: 0.18 }}
                fill={familyColors[c.family]}
                stroke={isActive ? "#FFF" : "#0F172A"}
                strokeWidth={isActive ? 3 : 2}
                opacity={isActive ? 1 : 0.85}
              />
              <text
                x={x}
                y={y + 1}
                textAnchor="middle"
                dominantBaseline="middle"
                fontFamily="Inter,system-ui"
                fontSize={isActive ? "16" : "14"}
                fontWeight="700"
                fill="#0F172A"
                style={{ pointerEvents: "none" }}
              >
                {c.letter}
              </text>
              {/* Pattern micro-glyph next to the bubble */}
              <text
                x={x}
                y={y + 32}
                textAnchor="middle"
                dominantBaseline="middle"
                fontFamily="JetBrains Mono,monospace"
                fontSize="9"
                fill={familyColors[c.family]}
                opacity="0.85"
                style={{ pointerEvents: "none" }}
              >
                {c.pattern}
              </text>
            </g>
          );
        })}
        {/* Centre label */}
        <text x={cx} y={cy - 4} textAnchor="middle" fontFamily="Cormorant Garamond,serif" fontSize="20" fontWeight="600" fill="#94A3B8">Circle of</text>
        <text x={cx} y={cy + 18} textAnchor="middle" fontFamily="Cormorant Garamond,serif" fontSize="20" fontWeight="600" fill="#94A3B8">Fifths</text>
      </svg>

      {/* Legend */}
      <div className="mt-3 grid grid-cols-2 gap-2 text-[11px] md:grid-cols-4">
        {(Object.entries(familyColors) as [string, string][]).map(([fam, color]) => {
          const f = FAMILY_INFO[Number(fam) as 1 | 2 | 3 | 4];
          return (
            <div key={fam} className="flex items-center gap-1.5">
              <span className="inline-block h-3 w-3 rounded-full" style={{ backgroundColor: color }} />
              <span className="font-semibold text-cyan-100">{f.label.split(" ")[0]}</span>
              <span className="ml-auto font-mono text-cyan-300/70">{f.chordList}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ── shared ── */
function StudyDeck({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="relative mb-4 rounded-2xl border border-cyan-400/30 bg-zinc-950/85 p-3 shadow-[0_8px_30px_rgba(0,0,0,0.5)]">
      <button onClick={() => setOpen((o) => !o)} className="flex w-full items-center justify-between text-left">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-amber-300">✦ Study deck</div>
          <div className="text-[12px] text-cyan-300/70">Key picker · master keyboard</div>
        </div>
        <span className="text-[12px] font-mono text-cyan-300">{open ? "▴" : "▾"}</span>
      </button>
      <motion.div initial={false} animate={{ height: open ? "auto" : 0, opacity: open ? 1 : 0 }} transition={{ duration: 0.25 }} style={{ overflow: "hidden" }}>
        <div className="mt-3 space-y-3">{children}</div>
      </motion.div>
    </div>
  );
}

function FadeIn({ children }: { children: ReactNode }) {
  const reduced = useReducedMotion();
  return (
    <motion.div initial={reduced ? false : { opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.55, ease: "easeOut" }}>
      {children}
    </motion.div>
  );
}

function PIQuote({ children, attribution }: { children: ReactNode; attribution?: string }) {
  return (
    <figure className="my-4 rounded-2xl border-l-4 border-amber-400/80 bg-zinc-900/60 px-5 py-4">
      <blockquote className="text-[17px] leading-relaxed text-cyan-50/95" style={{ fontFamily: '"Cormorant Garamond", serif', fontStyle: "italic" }}>
        {children}
      </blockquote>
      {attribution && <figcaption className="mt-2 text-[11px] uppercase tracking-[0.18em] text-amber-300/80">— {attribution}</figcaption>}
    </figure>
  );
}
